import React from "react";
import { View } from "react-native";

import { PROJECT_VENUE_TYPES, ProjectVenueType } from "@/types/db/projects";
import { ProjectForm } from "./types";
import useAuth from "@/hooks/useAuth";
import { ClockInOptions } from "@/components/screens/shared";
import { ClientInput } from "@/components/inputs/ClientInput";
import { LookupInput, type LookupInputOption } from "@/components/ui/LookupInput";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { CurrencyInput } from "@/components/ui/CurrencyInput";

interface ProjectFieldsProps {
  project: ProjectForm;
  locationMarket: { id: string; name: string; fips_code?: string } | null;
  updateProject: (patch: Partial<ProjectForm>) => void;
  onClientSelect: (clientId: string | null) => void;
  contactOptions: LookupInputOption[];
  onContactSelect: (field: "requested_by" | "primary_contact" | "onsite_contact", contactId: string | null) => void;
  locationOptions: LookupInputOption[];
  onLocationSelect: (locationId: string | null) => void;
  dressCodeOptions: LookupInputOption[];
  onDressCodeSelect: (dressCodeId: string | null) => void;
  marketIds: string[];
  onMarketsChange: (ids: string[]) => void;
}

export function ProjectFields({
  project,
  locationMarket,
  updateProject,
  onClientSelect,
  contactOptions,
  onContactSelect,
  locationOptions,
  onLocationSelect,
  dressCodeOptions,
  onDressCodeSelect,
  marketIds,
  onMarketsChange,
}: ProjectFieldsProps) {
  const { displayNames } = useAuth();

  return (
    <View style={{ flexDirection: "column", gap: 16, width: "100%" }}>
      {/* Client Selection */}
      <ClientInput
        label={`${displayNames?.clients?.name || "Client"} *`}
        value={project.client_id || null}
        placeholder={`Select ${displayNames?.clients?.name?.toLowerCase() || "client"}…`}
        onChange={onClientSelect}
      />

      {/* Contacts Row - requested_by, primary_contact, onsite_contact */}
      <View style={{ gap: 16, flexDirection: "row", flexWrap: "wrap" }}>
        <View style={{ flexBasis: "30%", flexGrow: 1 }}>
          <LookupInput
            label="Requested By"
            value={project.requested_by || null}
            options={contactOptions}
            state={project.client_id ? "editing" : "idle"}
            icon="account"
            placeholder="Select contact…"
            onChange={(id) => onContactSelect("requested_by", id)}
          />
        </View>
        <View style={{ flexBasis: "30%", flexGrow: 1 }}>
          <LookupInput
            label="Primary Contact"
            value={project.primary_contact || null}
            options={contactOptions}
            state={project.client_id ? "editing" : "idle"}
            icon="account"
            placeholder="Select contact…"
            onChange={(id) => onContactSelect("primary_contact", id)}
          />
        </View>
        <View style={{ flexBasis: "30%", flexGrow: 1 }}>
          <LookupInput
            label="Onsite Contact"
            value={project.onsite_contact || null}
            options={contactOptions}
            state={project.client_id ? "editing" : "idle"}
            icon="account"
            placeholder="Select contact…"
            onChange={(id) => onContactSelect("onsite_contact", id)}
          />
        </View>
      </View>

      {/* Location Selection */}
      <LookupInput
        label="Location"
        value={project.location_id || null}
        options={locationOptions}
        state="editing"
        icon="map-marker"
        placeholder="Select location…"
        onChange={onLocationSelect}
      />

      {/* Dress Code Selection */}
      <LookupInput
        label="Dress Code"
        value={project.meta_data?.dress_code_id || null}
        options={dressCodeOptions}
        state="editing"
        icon="tshirt-crew"
        placeholder="Select dress code…"
        onChange={onDressCodeSelect}
      />

      {/* Venue Type */}
      <LookupInput
        label="Venue Type"
        value={project.meta_data?.venue_type || null}
        options={PROJECT_VENUE_TYPES.map((vt) => ({ id: vt, name: vt, icon: "home-city" as const }))}
        state="editing"
        icon="home-city"
        placeholder="Select venue type"
        onChange={(value) => {
          updateProject({
            meta_data: {
              ...project.meta_data,
              venue_type: value as ProjectVenueType,
            },
          });
        }}
      />

      {/* Clock-In Options */}
      <ClockInOptions
        label={`${displayNames?.shifts?.name || "Shift"} Clock-In Method`}
        values={project.meta_data?.clock_in_options || {}}
        editing={true}
        onChange={(newValues) => {
          updateProject({
            meta_data: {
              ...project.meta_data,
              clock_in_options: newValues,
            },
          });
        }}
      />

      {/* Markets */}
      <MarketsInput
        value={marketIds}
        inheritedMarket={locationMarket}
        inheritedLabel="From Location"
        state="editing"
        onChange={onMarketsChange}
      />

      {/* Deposit Amount */}
      <CurrencyInput
        label="Deposit Amount"
        value={project.meta_data?.deposit_amount ?? null}
        onChange={(val) => {
          updateProject({
            meta_data: {
              ...project.meta_data,
              deposit_amount: val,
            },
          });
        }}
      />
    </View>
  );
}
